'use client';

import { Box, Button, Typography } from '@mui/material';
import React, { Dispatch, FunctionComponent, SetStateAction, useState } from 'react';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { DynamicResponseBody } from '../../../schemas/DynamicResponseBody';
import { SetURLSearchParams, useNavigate } from 'react-router-dom';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import PageContainer from '../tableComponents/PageContainer';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import { BasePartParameterValue } from '../../../schemas/BasePartParameterValue';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { FormattedMessage } from 'react-intl';

interface FilterButtonSectionProps {
    partCount: number;
    setGlobalFilterState: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    setFirstKeyAllowedMultipleSelect: Dispatch<SetStateAction<string | null>>;
    isApplyFiltersEnabled: boolean;
    basePartTrigger: Function;
    globalFilterState: DynamicRequestBody | undefined;
    handleDownloadCSV: Function;
    setAppliedKeys: Dispatch<SetStateAction<Set<string>>>;
    setApplyFilterResults: Dispatch<SetStateAction<DynamicResponseBody | undefined>>;
    setFilterResults: Dispatch<SetStateAction<DynamicResponseBody | undefined>>;
    handleApplyFilters: () => void;
    setProductChildField: Dispatch<SetStateAction<DetectedDefinition | undefined>>;
    setGlobalFilterStateAfterApply: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    setSearchParams: SetURLSearchParams;
    productField: DetectedDefinition;
    setPartCounts: Dispatch<SetStateAction<number | undefined>>;
    setResetFilterSignal: Dispatch<SetStateAction<number>>;
    setStoredSearchInput: () => void;
    setFilterKeyOrder: () => void;
    setSortedParts: Dispatch<SetStateAction<BasePartParameterValue[] | undefined>>;
    contentTree: ProductsContent;
}

const FilterButtonSection: FunctionComponent<FilterButtonSectionProps> = ({
    partCount,
    setGlobalFilterState,
    setFirstKeyAllowedMultipleSelect,
    isApplyFiltersEnabled,
    handleDownloadCSV,
    setAppliedKeys,
    setApplyFilterResults,
    handleApplyFilters,
    setFilterResults,
    setProductChildField,
    setGlobalFilterStateAfterApply,
    setSearchParams,
    productField,
    setPartCounts,
    setResetFilterSignal,
    setStoredSearchInput,
    setFilterKeyOrder,
    setSortedParts,
    contentTree,
}) => {
    const [isResetFiltersEnabled, setIsResetFiltersEnabled] = useState<boolean>(false);
    const navigate = useNavigate();

    const handleResetFilters = () => {
        setAppliedKeys(new Set());
        setApplyFilterResults(undefined);
        setFilterResults(undefined);
        setGlobalFilterState((prevState) => {
            if (!prevState) {
                return prevState;
            }
            return {
                '1': prevState['1'], // 保留 key 為 "1" 的值
            };
        });
        setFilterKeyOrder();
        setIsResetFiltersEnabled(false);
        setFirstKeyAllowedMultipleSelect(null);
        setProductChildField(undefined);
        setGlobalFilterStateAfterApply(undefined);
        setSearchParams(new URLSearchParams());
        setPartCounts(undefined);
        setStoredSearchInput();
        // 更新 reset 訊號（每次加 1，通知 FilterSection 重置內部狀態）
        setResetFilterSignal((prev) => prev + 1);
        setSortedParts([]);
        const newRoute = `/products/${productField.displayName}`;
        navigate(newRoute);
    };

    const { isMaxScreen, isScreenXxl, isScreenMd, isScreenSm, isScreenXs, responsiveWidth } =
        useResponsive();

    const shouldWrapWithPageContainer = !(isMaxScreen || isScreenXxl);

    const isMobileSize = isScreenSm || isScreenXs;

    const content = (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                paddingTop: '1rem',
                paddingLeft: shouldWrapWithPageContainer ? '0' : '1rem',
                paddingRight: shouldWrapWithPageContainer ? '0' : '2rem',
                paddingBottom: '1rem',
                justifyContent: 'space-between',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '1rem',
                }}
            >
                <Button
                    variant="text"
                    disabled={!isApplyFiltersEnabled}
                    onClick={handleApplyFilters}
                    sx={{
                        backgroundColor: isApplyFiltersEnabled ? '#B75332' : '#D3D3D3',
                        transition: 'none',
                        color: '#FFFFFF',
                        '&:hover': {
                            background: 'linear-gradient(0deg, #B75332 0%, #EE9769 100%)',
                        },
                    }}
                >
                    {contentTree.filters.buttonLabels.applyFilters.content}
                </Button>
                <Button
                    variant="contained"
                    disabled={isResetFiltersEnabled}
                    onClick={handleResetFilters}
                    sx={{
                        backgroundColor: '#494A4D',
                        backgroundImage: 'linear-gradient(to bottom, #595959, #494A4D)',
                        color: '#FFFFFF',
                        '&:hover': {
                            backgroundImage:
                                'linear-gradient(to bottom, #7F8487, #494A4D, #7F8487)',
                            backgroundColor: '#494A4D',
                        },
                    }}
                >
                    {contentTree.filters.buttonLabels.resetAll.content}
                </Button>
                <Typography variant="h6" sx={{ marginLeft: '1rem' }} data-testid="partcount_zone">
                    <span style={{ fontWeight: 500 }}>
                        {contentTree.filters.labels.resultCount.content}
                    </span>{' '}
                    <span style={{ fontWeight: 'bold' }}>{partCount.toLocaleString()}</span>
                </Typography>
            </Box>
            <Button
                variant="outlined"
                sx={{
                    borderRadius: '20px',
                    borderColor: '#000000',
                    color: '#000000',
                    backgroundColor: '#FFFFFF',
                    '&:hover': {
                        background: 'linear-gradient(180deg, #FFFFFF 0%, #CACBCD 100%)',
                        borderColor: '#000000',
                    },
                }}
                onClick={() => handleDownloadCSV()}
            >
                {contentTree.filters.buttonLabels.downloadTable.content}
            </Button>
        </Box>
    );

    const ipadContent = (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '0',
                justifyContent: 'space-between',
                paddingBottom: '1rem',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '1rem',
                }}
            >
                <Button
                    variant="text"
                    disabled={!isApplyFiltersEnabled}
                    onClick={handleApplyFilters}
                    sx={{
                        backgroundColor: isApplyFiltersEnabled ? '#B75332' : '#D3D3D3',
                        transition: 'none',
                        color: '#FFFFFF',
                        '&:hover': {
                            background: 'linear-gradient(0deg, #B75332 0%, #EE9769 100%)',
                        },
                    }}
                >
                    {contentTree.filters.buttonLabels.applyFilters.content}
                </Button>
                <Button
                    variant="contained"
                    disabled={isResetFiltersEnabled}
                    onClick={handleResetFilters}
                    sx={{
                        backgroundColor: '#494A4D',
                        backgroundImage: 'linear-gradient(to bottom, #595959, #494A4D)',
                        color: '#FFFFFF',
                        '&:hover': {
                            backgroundImage:
                                'linear-gradient(to bottom, #7F8487, #494A4D, #7F8487)',
                            backgroundColor: '#494A4D',
                        },
                    }}
                >
                    {contentTree.filters.buttonLabels.resetAll.content}
                </Button>
                <Typography variant="h6" sx={{ marginLeft: '1rem' }} data-testid="partcount_zone">
                    <span style={{ fontWeight: 500 }}>
                        {contentTree.filters.labels.resultCount.content}
                    </span>{' '}
                    <span style={{ fontWeight: 'bold' }}>{partCount.toLocaleString()}</span>
                </Typography>
            </Box>
            <Button
                variant="outlined"
                sx={{
                    borderRadius: '20px',
                    borderColor: '#000000',
                    color: '#000000',
                    backgroundColor: '#FFFFFF',
                    '&:hover': {
                        background: 'linear-gradient(180deg, #FFFFFF 0%, #CACBCD 100%)',
                        borderColor: '#000000',
                    },
                }}
                onClick={() => handleDownloadCSV()}
            >
                {contentTree.filters.buttonLabels.downloadTable.content}
            </Button>
        </Box>
    );

    const mobileContent = (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'column',
                padding: '0',
                gap: '0.5rem',
                paddingBottom: '1rem',
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    textAlign: 'center',
                    fontWeight: 400,
                    fontSize: '16px',
                    fontFamily: 'Poppins',
                }}
                data-testid="partcount_zone"
            >
                <FormattedMessage id="results.count" values={{ count: partCount }} />
            </Typography>
            <Button
                variant="text"
                disabled={!isApplyFiltersEnabled}
                onClick={handleApplyFilters}
                sx={{
                    backgroundColor: isApplyFiltersEnabled ? '#B75332' : '#D3D3D3',
                    transition: 'none',
                    color: '#FFFFFF',
                    '&:hover': {
                        background: 'linear-gradient(0deg, #B75332 0%, #EE9769 100%)',
                    },
                    width: responsiveWidth,
                    borderRadius: '10px',
                }}
            >
                {contentTree.filters.buttonLabels.applyFilters.content}
            </Button>
        </Box>
    );

    return shouldWrapWithPageContainer ? (
        <PageContainer
            sx={{ padding: 0, backgroundColor: '#F2F2F2' }}
            innerSx={{ width: responsiveWidth }}
            containerName={'filter-button-responsive'}
        >
            {!isMobileSize && !isScreenMd && content}
            {isScreenMd && ipadContent}
            {isMobileSize && mobileContent}
        </PageContainer>
    ) : (
        content
    );
};

export default FilterButtonSection;
